import { motion } from "framer-motion";
import { InstagramLogo, VideoCamera, At, Gift } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";

const INSTAGRAM_URL = "https://instagram.com/ig.regalaunamelodia";
const icons = [VideoCamera, At, Gift];

export default function InstagramPromo() {
  const { t } = useLang();
  const steps = t("instagram.steps").map((text, i) => ({ icon: icons[i], text }));
  return (
    <section className="relative py-20 max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ type: "spring", stiffness: 110, damping: 18 }}
        className="relative rounded-[3rem] overflow-hidden p-10 sm:p-16"
        style={{ background: "linear-gradient(120deg, hsl(351 100% 82%), hsl(298 55% 80%), hsl(200 80% 82%))" }}
      >
        <div className="blob w-[280px] h-[280px] -top-10 right-10" style={{ background: "hsl(45 100% 82%)" }} />
        <div className="relative grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full glass border border-white/60 px-4 py-2 mb-5">
              <InstagramLogo weight="fill" size={20} className="text-foreground" />
              <span className="text-sm font-bold text-foreground/80">{t("instagram.badge")}</span>
            </div>
            <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight text-foreground leading-[0.95]">
              {t("instagram.title1")} <span className="font-hand text-5xl sm:text-6xl">{t("instagram.title2")}</span> {t("instagram.title3")}
            </h2>
            <p className="mt-5 text-foreground/70 font-semibold max-w-md">
              {t("instagram.desc1")} <span className="font-bold">@ig.regalaunamelodia</span>.
            </p>
            <motion.a
              data-testid="instagram-cta"
              href={INSTAGRAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05, rotate: -1 }}
              whileTap={{ scale: 0.95 }}
              className="mt-8 inline-flex items-center gap-2 rounded-full bg-foreground px-8 py-4 font-bold text-background shadow-soft-lg"
            >
              <InstagramLogo weight="fill" size={22} /> {t("instagram.follow")}
            </motion.a>
          </div>

          <div className="space-y-4">
            {steps.map((s, i) => {
              const Icon = s.icon;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.12 }}
                  className="flex items-center gap-4 rounded-3xl glass border border-white/50 p-5 shadow-glass"
                >
                  <div className="flex-shrink-0 w-12 h-12 rounded-2xl bg-white flex items-center justify-center">
                    <Icon weight="duotone" size={26} className="text-primary" />
                  </div>
                  <p className="font-bold text-foreground/80 text-sm">{s.text}</p>
                  <span className="ml-auto font-display font-bold text-3xl text-white/60">{i + 1}</span>
                </motion.div>
              );
            })}
          </div>
        </div>
      </motion.div>
    </section>
  );
}
